<template>
  <div class="text-textPrimary text-left">
    <div class="flex items-start justify-between gap-4">
      <div>
        <p class="font-bold">Status</p>
        <StatusBadge
          :status="(details?.status as DSAttendanceStatus) ?? DSAttendanceStatus.PENDING_SUBMISSION"
        />
      </div>

      <!--
        Eixo 4 da discovery 2026-09-11 — encadear registros em vez de voltar à lista. Só
        aparece quando o backend confirmou que existe próximo com os filtros que trouxeram o
        auditor até aqui: um botão que às vezes não leva a lugar nenhum é pior que nenhum.
      -->
      <OutlineButton
        v-if="hasNextContainer"
        data-testid="ds-attendance-next-container-button"
        text="Próximo registro ›"
        width="180px"
        height="40px"
        :loading="isGoingToNext"
        @click="handleGoToNext"
      />
    </div>

    <!--
      F3/§6 — variante "Solicitação automática". Só existe quando o job do minuto 7 abriu a
      cobrança (`requestedLeg`): sem ela, e em todo backend anterior à F3, a tela é a de hoje.

      Fica ACIMA do grid de sempre, e não no lugar dele: o auditor precisa continuar vendo
      data, escala, unidade e turnos — o bloco só responde "o que está sendo cobrado, de quem,
      por qual canal e até quando".
    -->
    <div
      v-if="isAutomaticRequest"
      data-testid="ds-attendance-shipment-request"
      class="my-4 rounded-lg border p-4"
      :class="isRequestOverdue ? 'border-negative bg-red-50' : 'border-alert bg-orange-50'"
    >
      <div class="flex flex-wrap items-center gap-2">
        <p class="font-bold">Solicitação automática</p>
        <span
          v-if="isRequestOverdue"
          data-testid="ds-attendance-shipment-request-overdue"
          class="inline-flex items-center rounded-full border border-negative bg-red-50 px-2 py-0.5 text-[10px] font-bold text-negative whitespace-nowrap"
          title="O prazo da política passou. A solicitação continua aberta — vencer não bloqueia aprovação, edição nem pagamento."
        >
          Vencida
        </span>
      </div>

      <div class="mt-2 flex flex-col gap-0.5 text-sm">
        <span data-testid="ds-attendance-shipment-request-leg">
          Perna cobrada: <b>{{ requestedLegLabel }}</b>
        </span>
        <span data-testid="ds-attendance-shipment-request-channel">
          Canal esperado: <b>{{ requestChannelLabel }}</b>
        </span>
        <span v-if="formattedDeadline" data-testid="ds-attendance-shipment-request-deadline">
          Prazo da política: <b>até {{ formattedDeadline }}</b>
        </span>
        <span v-if="formattedOpenedAt" data-testid="ds-attendance-shipment-request-opened-at">
          Aberta em <b>{{ formattedOpenedAt }}</b>
        </span>
      </div>

      <!-- Mesmo texto do card da fila (M3, balão ③): não há o que desfazer à mão quando a
           perna for coberta por outro caminho. -->
      <p class="mt-2 text-xs text-neutral50">
        Cancelada sozinha se chegar folha aprovada, check tardio do app, App Externo ou envio manual
        que cubra a perna.
      </p>
    </div>

    <div class="flex w-1/2 justify-between my-4">
      <div class="flex flex-col gap-4">
        <div>
          <p class="font-bold">Data</p>
          <p>{{ formattedDate }}</p>
        </div>

        <div>
          <p class="font-bold">Escalas</p>
          <p>{{ details?.schedule?.name ?? 'Não informada' }}</p>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div>
          <p class="font-bold">Unidade</p>
          <p>{{ details?.healthUnit?.name ?? 'Não informada' }}</p>
        </div>

        <div>
          <p class="font-bold">Turnos</p>
          <p>
            {{
              details?.shifts?.length
                ? details.shifts.map((s) => s.name).join(', ')
                : 'Não informado'
            }}
          </p>
        </div>
      </div>
    </div>

    <DsAttendanceHistoryCard title="Histórico" :events="historyEvents" />

    <CommentsForm :attendance-id="String(route.params.attendanceId)" :can-comment="canEmitRecord" />

    <div class="flex justify-end gap-4">
      <FilledButton
        text="Fechar"
        width="80px"
        height="48px"
        class="!bg-primary30"
        @click="router.push(approvalsQueueLocation('attendance'))"
      />
      <!--
        CTA da cobrança. Leva ao fluxo de envio que JÁ existe (o `SendRecordModal` da W2/W4) —
        aqui ele pode abrir de verdade porque esta tela conhece a vaga cobrada, que é
        exatamente o contexto que faltava na fila (I7/§8.2) e o motivo de o card da T10 mandar
        o auditor para cá. Sem a vaga no detalhe (backend anterior à F3) o botão fica
        desabilitado com o motivo no tooltip, em vez de abrir um passo 2 sem saída.
      -->
      <OutlineButton
        v-if="isAutomaticRequest"
        data-testid="ds-attendance-shipment-send-record"
        text="Enviar registro"
        width="180px"
        height="48px"
        :disabled="!canEmitRecord || !sendRecordContext"
        :title="sendRecordTooltip"
        @click="openSendRecord"
      />
      <!-- `containers/:id/delete` exige `attendance_advanced_receiving`. -->
      <OutlineButton
        v-if="!isDeleted && canRevertValidation"
        text="Excluir pendência"
        width="180px"
        height="48px"
        class="!border-negative !text-negative !bg-white"
        @click="isDeleteModalOpen = true"
      />
    </div>
  </div>

  <!--
    M2 — o modal de envio é PESADO (5 passos, dropzone e visualizador) e serve uma minoria dos
    registros. Assíncrono + `v-if`: quem nunca clica em "Enviar registro" não baixa nem executa
    a árvore da W4. Mesma montagem do Detalhes do Plantão (W9).
  -->
  <SendRecordModal
    v-if="isSendRecordModalOpen && sendRecordContext"
    :model-value="isSendRecordModalOpen"
    :context="sendRecordContext"
    :doctor-station-id="sendRecordContext.doctorStationId"
    :schedule-id="sendRecordContext.scheduleId"
    @update:model-value="handleSendRecordModalToggle"
    @saved="handleSendRecordSaved"
    @redirect-to-sheet="handleRedirectToSheet"
  />

  <!--
    C6 — "Registro de Plantão" no passo 1 do modal reabre o wizard de folha AQUI, com os campos
    da vaga preenchidos e travados. Responder à cobrança com uma folha é caminho legítimo.
  -->
  <AttendanceUploadModal
    v-if="isSheetUploadModalOpen"
    v-model="isSheetUploadModalOpen"
    :initial-form="sheetInitialForm"
    :locked-fields="sheetLockedFields"
    @save="handleSheetSaved"
  />

  <ConfirmDeleteAction
    :show="isDeleteModalOpen"
    :isLoading="isDeleting"
    :showJustification="true"
    title="Excluir pendência"
    description="Você tem certeza que deseja excluir essa pendência? Essa ação não poderá ser desfeita."
    textButton="Excluir pendência"
    @closeModal="isDeleteModalOpen = false"
    @handleConfirm="handleDeleteConfirm"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineAsyncComponent } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'
import DsAttendanceHistoryCard, {
  HistoryEvent
} from '@/components/designSystem/Cards/DsAttendanceHistoryCard.vue'
import CommentsForm from '@/components/designSystem/Inputs/CommentsForm.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import ConfirmDeleteAction from '@/components/designSystem/modals/confirm.delete.action.vue'
import {
  ACTIVITY_REQUEST_CHANNEL_LABELS,
  ActivityRequestChannel,
  CoveredLegs,
  DSAttendanceStatus,
  REQUESTED_LEG_LABELS
} from '@/constants/ds-attendance.constants'
import { mapAuditTrailToHistoryEvents } from '@/utils/dsAttendanceHistory'
import type { DSAttendanceContainerDetailResponse } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import {
  approvalsQueueBreadcrumbs,
  approvalsQueueLocation
} from '@/modules/company/Approvals/Hub/queues'
import { buildSheetHandoff } from '@/components/designSystem/modals/SendRecordModal/types'
import type {
  RedirectToSheetPayload,
  SendRecordContext,
  SendRecordStationOption
} from '@/components/designSystem/modals/SendRecordModal/types'
import type { DsAttendanceUploadForm } from '@/components/designSystem/modals/AttendanceUploadModal/types'
import { DEFAULT_UTC_OFFSET, formatDateTimeWithOffset } from '@/utils/date'
import { toDDMMYYYY } from '../utils/activity-time'
import { routeNameForStatus } from '../utils/status-route-map'
import { useNextContainer } from '../composables/useNextContainer'
import { useCanEmitAttendance } from '../composables/useCanEmitAttendance'
import { useCanRevertValidation } from '../composables/useCanRevertValidation'

const SendRecordModal = defineAsyncComponent(
  () => import('@/components/designSystem/modals/SendRecordModal/Index.vue')
)
const AttendanceUploadModal = defineAsyncComponent(
  () => import('@/components/designSystem/modals/AttendanceUploadModal/Index.vue')
)

/**
 * F3 (§6.4) — os campos da solicitação automática no detalhe do container. Ficam num tipo
 * LOCAL porque `DsAttendancePolicyResponses.ts` está fora da lista de arquivos desta task
 * (mesma escolha da T9 no service e da F2 em `DsAttendanceActivityAudit.vue`): quando o
 * contrato do backend estabilizar, é um move mecânico.
 *
 * Todos opcionais: a tela precisa continuar de pé em backend anterior à F3.
 */
interface AwaitingShipmentDetail extends DSAttendanceContainerDetailResponse {
  requestedLeg?: CoveredLegs | string | null
  requestChannel?: ActivityRequestChannel | string | null
  /** Prazo da cobrança — a F3 reaproveita o "vencido" do container (§6.4), sem campo novo. */
  expectedSubmissionAt?: string | null
  /** Quando o job abriu a cobrança: a prova de que quem abriu foi o sistema. */
  createdAt?: string | null
  companyId?: number | null
  /**
   * O campo REAL do contrato: `ContainerDetailResponseDto` devolve a vaga como escalar no
   * topo (`doctorStationId`), lido direto da coluna do container — e por isso preenchido
   * também numa solicitação, que nasce SEM entry (§6.4). O objeto `doctorStation` abaixo é a
   * forma rica que a spec §3.4 prevê e o backend ainda não serializa: quando chegar, os dados
   * do plantão (médico, horários) enriquecem o modal; até lá, o id sozinho já o abre.
   */
  doctorStationId?: number | null
  doctorStation?: {
    id?: number | null
    doctorName?: string | null
    doctorCrm?: string | null
    date?: string | null
    shiftName?: string | null
    expectedStart?: string | null
    expectedEnd?: string | null
    healthUnitName?: string | null
    scheduleName?: string | null
    /** Offset do plantão (`-03:00`), para ler `createdAt` — que é instante UTC, não parede. */
    timezone?: string | null
  } | null
}

const router = useRouter()
const headerStore = useHeaderStore()
const route = useRoute()
const toast = useToast()
const { notifyError } = useErrorToast()

const details = ref<AwaitingShipmentDetail | null>(null)
const isDeleted = computed(() => details.value?.status === DSAttendanceStatus.DELETED)

/**
 * F3 — é uma cobrança quando a política pediu uma perna. `requestedLeg` sozinho basta: esta
 * rota já é a do container aguardando envio, e nenhum backend anterior à F3 devolve o campo.
 */
const isAutomaticRequest = computed(() => !!details.value?.requestedLeg)

/** Catálogo local primeiro; código fora dele aparece cru — rótulo vazio esconderia a cobrança. */
const requestedLegLabel = computed(() => {
  const leg = details.value?.requestedLeg
  return (leg ? REQUESTED_LEG_LABELS[leg] : null) ?? leg ?? 'Perna não informada'
})

const requestChannelLabel = computed(() => {
  const channel = details.value?.requestChannel
  return (
    ACTIVITY_REQUEST_CHANNEL_LABELS[channel as ActivityRequestChannel] ??
    channel ??
    'Canal não informado'
  )
})

/**
 * Offset do plantão. O escalar do topo (`details.timezone`) é o campo que
 * `DSAttendanceContainerDetailResponseDto` REALMENTE serve — o objeto rico
 * `doctorStation.timezone` é a forma que a spec §3.4 prevê e que o backend ainda não
 * serializa (comentário acima, na `interface`). Ler só o objeto rico fazia o prazo cair
 * SEMPRE em `-03:00` fora de Brasília, 1–2 h divergente do card da fila (achado Opus
 * W-N1); Brasília entra por último, só quando nem o container nem o objeto rico têm nada.
 */
const stationOffset = computed(
  () => details.value?.timezone ?? details.value?.doctorStation?.timezone ?? DEFAULT_UTC_OFFSET
)

/*
 * R2-I4 (achado Opus IMPORTANT-1) — `expectedSubmissionAt` é um INSTANTE real: nasce de
 * `calculateExpectedSubmissionAtTzAware` (`zonedTimeToUtc(23:59 local do plantão)` + horas),
 * não de `station.end_hour`, e a API o serve cru (`toISOString()`). Lido como parede
 * literal, como esta tela fazia, o mesmo prazo de 10:00 em Brasília aparecia 10:00 no card
 * da fila e 13:00 aqui — e "Vencida" acendia em horas diferentes nas duas. A régua é a
 * mesma do `DsAttendanceRequestCard`: o fuso do plantão.
 */
const formattedDeadline = computed(() =>
  details.value?.expectedSubmissionAt
    ? formatDateTimeWithOffset(details.value.expectedSubmissionAt, stationOffset.value)
    : ''
)

/*
 * `createdAt` (N1): `created_at` é `@CreateDateColumn` do `BaseModel`, um instante REAL em
 * UTC. Lido como parede, uma cobrança aberta às 16:07 na unidade aparecia como 19:07. Aqui
 * vale o offset do plantão, como o `DsAttendanceConciliationModal` e a
 * `StationActivityRecordsSection` já fazem.
 */
const formattedOpenedAt = computed(() => {
  const iso = details.value?.createdAt
  return iso ? formatDateTimeWithOffset(iso, stationOffset.value) : ''
})

/** Vencer NÃO bloqueia nada (§6): é prioridade visual, não trava. */
const isRequestOverdue = computed(() => {
  const iso = details.value?.expectedSubmissionAt
  if (!iso) return false
  const deadline = new Date(iso).getTime()
  return !Number.isNaN(deadline) && deadline <= Date.now()
})

const formattedDate = computed(
  () => toDDMMYYYY(details.value?.referenceDate) || details.value?.referenceDate || 'Não informada'
)

const isDeleteModalOpen = ref(false)
const isDeleting = ref(false)
const historyEvents = ref<HistoryEvent[]>([])

/**
 * Eixo 4 — "Próximo registro". `hasNextContainer` é resolvido uma vez ao abrir a tela: a fila
 * pode mudar embaixo, e é por isso que `goToNext` consulta o backend de novo no clique.
 */
const hasNextContainer = ref(false)
const isGoingToNext = ref(false)
const { fetchNextId, goToNext } = useNextContainer()

async function handleGoToNext() {
  if (isGoingToNext.value) return
  isGoingToNext.value = true
  try {
    const moved = await goToNext(String(route.params.attendanceId))
    // Fila acabou entre a abertura da tela e o clique: volta para a lista em vez de deixar o
    // auditor olhando um botão morto.
    if (!moved) {
      hasNextContainer.value = false
      router.push(approvalsQueueLocation('attendance'))
    }
  } finally {
    isGoingToNext.value = false
  }
}

/**
 * F3 — o header default fala de folha de ponto; uma cobrança de Registro de Atividade não é
 * folha. Só troca depois do fetch, e só quando `requestedLeg` veio: sem ele o header é o de
 * hoje, palavra por palavra.
 */
function applyHeader() {
  if (isAutomaticRequest.value) {
    headerStore.setTitle(
      'Solicitação de Registro de Atividade',
      'Solicitação automática aguardando o envio do registro',
      approvalsQueueBreadcrumbs('attendance')
    )
    return
  }
  headerStore.setTitle(
    'Envio de Folha de Ponto',
    'Registro aguardando o envio da folha de monitoramento',
    approvalsQueueBreadcrumbs('attendance')
  )
}

async function loadDetails(attendanceId: string) {
  try {
    const response = await Network.dsAttendance.getById(attendanceId)
    details.value = response as AwaitingShipmentDetail
  } catch (error) {
    console.error('Erro ao buscar detalhes do container:', error)
    notifyError(error, 'Não foi possível carregar os detalhes do registro de presença.')
  }
  applyHeader()
}

async function loadHistory(attendanceId: string) {
  try {
    const response = await Network.dsAttendance.getAuditTrail(attendanceId)
    historyEvents.value = mapAuditTrailToHistoryEvents(response.data)
  } catch (error) {
    // Antes só o console sabia: o card de histórico ficava vazio como se o registro
    // não tivesse eventos, sem distinguir "sem histórico" de "falhou ao buscar".
    console.error('Erro ao buscar histórico do container:', error)
    notifyError(error, 'Não foi possível carregar o histórico do registro de presença.')
  }
}

onMounted(async () => {
  // Sem isto a tela herdava o header da página anterior (título e breadcrumb errados).
  applyHeader()
  const attendanceId = String(route.params.attendanceId)

  await loadDetails(attendanceId)

  // Não bloqueia o carregamento da tela: `fetchNextId` já engole a falha e devolve `null`.
  hasNextContainer.value = (await fetchNextId(attendanceId)) !== null

  await loadHistory(attendanceId)
})

/* -------------------------------------------------------------------------- *
 * F3 — responder à cobrança sem sair da tela
 * -------------------------------------------------------------------------- */

const isSendRecordModalOpen = ref(false)
const isSheetUploadModalOpen = ref(false)
const sheetInitialForm = ref<Partial<DsAttendanceUploadForm> | null>(null)
const sheetLockedFields = ref<string[]>([])

/**
 * A vaga cobrada, no formato que o modal da W4 espera. `null` quando o detalhe não traz a
 * vaga (backend anterior à F3): sem `doctorStationId` o modal abriria com `0` e o passo 2
 * ficaria sem saída (I7) — melhor um botão desabilitado com o motivo do que um beco.
 */
function buildSendRecordStation(
  detail: AwaitingShipmentDetail,
  doctorStationId: number
): SendRecordStationOption {
  // Campos ausentes ficam `undefined` de propósito: `SendRecordStationOption` os declara
  // opcionais, e normalizar tudo para `null` só somaria ramos sem mudar o que o modal lê.
  const station = detail.doctorStation ?? {}
  return {
    doctorStationId,
    referenceDate: station.date ?? detail.referenceDate,
    expectedStart: station.expectedStart,
    expectedEnd: station.expectedEnd,
    doctorName: station.doctorName,
    scheduleName: station.scheduleName ?? detail.schedule?.name,
    healthUnitName: station.healthUnitName ?? detail.healthUnit?.name,
    shiftName: station.shiftName,
    // A perna cobrada é literalmente a perna que falta — é ela que o passo 4 pré-marca.
    missingLegs: (detail.requestedLeg as CoveredLegs) ?? null
  }
}

const sendRecordContext = computed<SendRecordContext | null>(() => {
  const detail = details.value
  // O escalar do topo primeiro: é o que o backend realmente devolve hoje. O `doctorStation.id`
  // fica como segunda leitura para o dia em que o objeto rico da §3.4 chegar.
  const doctorStationId = Number(detail?.doctorStationId ?? detail?.doctorStation?.id ?? 0)
  const scheduleId = Number(detail?.schedule?.id ?? 0)
  if (!detail || !doctorStationId || !scheduleId) return null

  return {
    doctorStationId,
    scheduleId,
    companyId: detail.companyId ?? null,
    healthUnitId: detail.healthUnit?.id ?? null,
    referenceDate: detail.referenceDate ?? null,
    station: buildSendRecordStation(detail, doctorStationId)
  }
})

/**
 * Enviar registro exige `attendance_emitting`, nos três recursos de presença —
 * mesmo critério de `canEmitSheetRecord` na fila e de `canEmitActivityRecord` no
 * Detalhes do Plantão. Esta tela é o segundo caminho até o wizard de folha: o
 * passo 1 do `SendRecordModal` bifurca para ele (C6), e nem o modal nem o wizard
 * checam permissão.
 */
const canEmitRecord = useCanEmitAttendance()
const canRevertValidation = useCanRevertValidation()

const sendRecordTooltip = computed(() => {
  if (!canEmitRecord.value) return 'Você não tem permissão para enviar registros.'
  if (!sendRecordContext.value)
    return 'Envie pelo Detalhes do Plantão: este registro não traz a vaga de origem.'
  return undefined
})

function openSendRecord() {
  if (!canEmitRecord.value || !sendRecordContext.value) return
  isSendRecordModalOpen.value = true
}

/**
 * Recarrega detalhe e histórico e diz se o container saiu de "aguardando envio". O backend
 * responde **201** e, quando o plantão tinha solicitação aberta, é ESTE container que é
 * reusado: vira `PENDING_AUDIT` com `CONTAINER_SUBMIT` + `REQUEST_FULFILLED` na trilha (§6.5).
 *
 * Se o `coveredLegs` enviado não cobrir a perna cobrada, o container CONTINUA
 * `PENDING_SUBMISSION` (§6.5, fim) e a tela permanece onde está, agora com o histórico novo.
 */
async function reloadAfterSubmission(): Promise<string | null> {
  const attendanceId = String(route.params.attendanceId)
  await loadDetails(attendanceId)
  await loadHistory(attendanceId)

  const routeName = routeNameForStatus(details.value?.status ?? null)
  return routeName && routeName !== 'DsAttendancePendingSubmission' ? routeName : null
}

/** Rota do novo status, guardada até o modal fechar (ver `handleSendRecordSaved`). */
const pendingRouteAfterSend = ref<string | null>(null)

function leaveTo(routeName: string) {
  router.push({
    name: routeName,
    params: { attendanceId: String(route.params.attendanceId) },
    query: { ...route.query }
  })
}

/**
 * Envio aceito pelo `SendRecordModal`. O modal emite `saved` JUNTO com a transição para
 * `SEND_RECORD_STEP.SUCCESS` — a tela que a §8.2 exige, com os `#A-` criados e os botões
 * "Voltar à escala" / "Ver na fila de auditoria". Fechar o modal aqui matava essa tela: o
 * auditor via só um toast e caía na fila sem nunca ver o número do registro criado. Então
 * recarregamos por baixo e adiamos a navegação para o fechamento — o mesmo contrato do
 * `stationDetailsModal` do Detalhes do Plantão (F2), que só recarrega os dados.
 */
async function handleSendRecordSaved() {
  pendingRouteAfterSend.value = await reloadAfterSubmission()
}

/** Fechou o modal: agora sim a tela segue para a rota do novo status, com os filtros junto. */
function handleSendRecordModalToggle(open: boolean) {
  isSendRecordModalOpen.value = open
  if (open) return

  const routeName = pendingRouteAfterSend.value
  pendingRouteAfterSend.value = null
  if (routeName) leaveTo(routeName)
}

/**
 * Folha enviada pelo wizard do C6. Este modal não tem tela de sucesso própria: fecha, avisa
 * e segue, que é o comportamento de sempre do "Registro de Plantão".
 */
async function handleSheetSaved() {
  isSheetUploadModalOpen.value = false
  toast.success('Registro enviado com sucesso.')

  const routeName = await reloadAfterSubmission()
  if (routeName) leaveTo(routeName)
}

/** C6 — o passo 1 bifurca: "Registro de Plantão" reabre o wizard de folha aqui mesmo. */
function handleRedirectToSheet(payload: RedirectToSheetPayload) {
  isSendRecordModalOpen.value = false
  // O wizard de folha não checa permissão em passo nenhum: quem gateia é quem abre.
  if (!canEmitRecord.value) return
  const handoff = buildSheetHandoff(payload)
  sheetInitialForm.value = handoff.form
  sheetLockedFields.value = handoff.lockedFields
  isSheetUploadModalOpen.value = true
}

async function handleDeleteConfirm(justification?: string) {
  const attendanceId = String(route.params.attendanceId)
  isDeleting.value = true
  try {
    await Network.dsAttendance.deleteContainer(attendanceId, { justification: justification! })
    isDeleteModalOpen.value = false
    toast.success('Registro excluído com sucesso.')
    router.push(approvalsQueueLocation('attendance'))
  } catch (error) {
    console.error('Erro ao excluir o registro:', error)
    notifyError(error, 'Não foi possível excluir o registro de presença.')
  } finally {
    isDeleting.value = false
  }
}
</script>
